import { useEffect, useState } from 'react';

import loadApp, { App } from './app';
import loadAppLauncher from './app-launcher';
import loadBarcodeScanner from './barcode-scanner';
import loadBiometric from './biometric';
import loadBrowser from './browser';
import loadCamera from './camera';
import loadCore from './core';
import loadDevice from './device';
import loadHaptics from './haptics';
import loadKeyboard from './keyboard';
import loadNetwork from './network';
import loadPreferences from './preferences';
import loadSplashScreen from './splash-screen';

enum CapacitorVersion {
  v8_0 = 'capacitor-v8_0',
}

// Add new versions at the top of the object. Anything not listed will be marked as deprecated.
const versionLoaders = {
  [CapacitorVersion.v8_0]: async () =>
    (await import(/* webpackChunkName: "capacitor-v8" */ './v8')).default,
};

type SupportedCapacitorVersion = keyof typeof versionLoaders;

const supportedVersions: SupportedCapacitorVersion[] = [CapacitorVersion.v8_0];

const getVersion = () => {
  // On web there is no native shell to "update", so always treat it as the latest supported
  // version. We can't rely on `window.Capacitor` being undefined to detect web — the Capacitor
  // web runtime defines it on web too — so use Capacitor's own platform detection instead.
  // Native builds include a `capacitor-v*` token in the userAgent, which we match below.
  if (!window.Capacitor?.isNativePlatform?.()) {
    return supportedVersions[0];
  }

  return Object.values(CapacitorVersion).find(version => navigator.userAgent.includes(version));
};

/**
 * This hook should only be called once as its required, calling it more than once can lead to performance
 * issues as it will try to load the capacitor plugins all again, to avoid the situation we throw an error
 * as soon as the hook is being called again.
 */
const useRegisterCapacitorPlugins = () => {
  const [isRegistering, setIsRegistering] = useState(true);
  const [isObsoleteVersion, setIsObsoleteVersion] = useState(false);

  useEffect(() => {
    if (App) {
      throw Error('useRegisterCapacitorPlugins should only be called once.');
    }

    const effect = async (version: SupportedCapacitorVersion) => {
      const plugins = await versionLoaders[version]();

      loadApp(plugins.App);
      loadAppLauncher(plugins.AppLauncher);
      loadBarcodeScanner(plugins.BarcodeScanner);
      loadBrowser(plugins.Browser);
      loadCamera(plugins.Camera);
      loadCore(plugins.Core);
      loadDevice(plugins.Device);
      loadKeyboard(plugins.Keyboard);
      loadNetwork(plugins.Network);
      loadPreferences(plugins.Preferences);
      loadSplashScreen(plugins.SplashScreen);
      loadHaptics(plugins.Haptics);
      loadBiometric(plugins.NativeBiometric);

      setIsRegistering(false);
    };

    const version = getVersion();
    if (version && supportedVersions.includes(version)) {
      // skipcq: JS-0328
      effect(version as SupportedCapacitorVersion);
    } else {
      setIsObsoleteVersion(true);
      // If we're forcing an incompatible version, we should load the oldest supported version.
      // skipcq: JS-0328
      effect(supportedVersions[supportedVersions.length - 1] as SupportedCapacitorVersion);
    }
  }, []);

  return {
    isRegistering,
    isObsoleteVersion,
  };
};

export default useRegisterCapacitorPlugins;
